// MosqueLocationSelector.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useDropdownData } from "../../../src/components/deliveryLoctions/useDropdownData";
import GenericCombobox from "../../../src/components/deliveryLoctions/GenericCombobox";

import { getStates, getCities, getAreas } from "../../utils/SonicAPIV2";

import {
  UilLocationPinAlt,
  UilLocationPoint,
  UilMapMarker,
  UilMapPin,
} from "@iconscout/react-unicons";

const FIXED_COUNTRY = { countryId: 1, countryName: "UAE" };
const DEFAULT_STATE_ID = 1;
const DEFAULT_CITY_ID = 13;

export default function MosqueLocationSelector({ onChange, initialArea }) {
  const countries = useMemo(() => [FIXED_COUNTRY], []);

  const [selectedCountry] = useState(FIXED_COUNTRY);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedArea, setSelectedArea] = useState(initialArea || null);

  // --- Load states for country 1
  const { data: states } = useDropdownData(getStates, [1]);

  // Auto-select stateId=1 ONCE when states list first arrives
  const didInitStateRef = useRef(false);
  useEffect(() => {
    if (didInitStateRef.current) return;
    if (!states?.length) return;

    const match = states.find((s) => s.stateId === DEFAULT_STATE_ID) || null;
    setSelectedState(match);
    didInitStateRef.current = true;
  }, [states]);

  // --- Load cities for selectedStateId
  const stateId = selectedState?.stateId;
  const { data: cities } = useDropdownData(getCities, [stateId]);

  // Auto-select cityId=13 ONCE when cities list first arrives
  const didInitCityRef = useRef(false);
  useEffect(() => {
    if (didInitCityRef.current) return;
    if (!cities?.length) return;

    const match = cities.find((c) => c.cityId === DEFAULT_CITY_ID) || null;
    setSelectedCity(match);
    didInitCityRef.current = true;
  }, [cities]);

  // --- Load areas for selectedCityId
  const cityId = selectedCity?.cityId;
  const { data: areas } = useDropdownData(getAreas, [cityId]);

  // When user selects area, notify parent ONLY when areaId changes
  const lastSentAreaIdRef = useRef(null);

  // Seed selectedArea from the remembered value ONCE areas list arrives
  const didInitAreaFromPropRef = useRef(false);
  useEffect(() => {
    if (didInitAreaFromPropRef.current) return;
    if (!initialArea?.areaId) {
      didInitAreaFromPropRef.current = true;
      return;
    }
    if (!areas?.length) return;

    const match =
      areas.find((a) => a.areaId === initialArea.areaId) || initialArea;
    setSelectedArea(match);
    lastSentAreaIdRef.current = match.areaId; // don't re-fire onChange for the remembered value
    didInitAreaFromPropRef.current = true;
  }, [areas, initialArea]);

  useEffect(() => {
    const areaId = selectedArea?.areaId;
    if (!areaId) return;

    if (lastSentAreaIdRef.current === areaId) return;
    lastSentAreaIdRef.current = areaId;

    onChange?.({
      country: selectedCountry,
      state: selectedState,
      city: selectedCity,
      area: selectedArea,
    });
    // Intentionally ONLY depends on selectedArea?.areaId to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea?.areaId]);

  return (
    <div className="mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-4">
      {/* Country (fixed) */}
      <div className="flex flex-row items-center mt-2">
        <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
          <UilLocationPinAlt className="text-gray-500 w-4" />
          <div className="font-normal text-C12 w-12">Country</div>
        </div>

        <GenericCombobox
          label=""
          options={countries}
          selected={selectedCountry}
          onChange={() => {}}
          displayKey="countryName"
          keyField="countryId"
          disabled={true}
        />
      </div>

      {/* State (auto-selected, disabled) */}
      <div className="flex flex-row items-center mt-2">
        <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
          <UilLocationPoint className="text-gray-500 w-4" />
          <div className="font-normal text-C12 w-12">State</div>
        </div>

        <GenericCombobox
          label=""
          options={states || []}
          selected={selectedState}
          onChange={() => {}}
          displayKey="statessName"
          keyField="stateId"
          disabled={true}
        />
      </div>

      {/* City (auto-selected, disabled) */}
      <div className="flex flex-row items-center mt-2">
        <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
          <UilMapMarker className="text-gray-500 w-4" />
          <div className="font-normal text-C12 w-12">City</div>
        </div>

        <GenericCombobox
          label=""
          options={cities || []}
          selected={selectedCity}
          onChange={() => {}}
          displayKey="cityName"
          keyField="cityId"
          disabled={true}
        />
      </div>

      {/* Area (selectable) */}
      <div className="flex flex-row items-center mt-2">
        <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
          <UilMapPin className="text-gray-500 w-4" />
          <div className="font-normal text-C12 w-12">Area</div>
        </div>

        <GenericCombobox
          label=""
          options={areas || []}
          selected={selectedArea}
          onChange={(val) => setSelectedArea(val)}
          displayKey="areaName"
          keyField="areaId"
          disabled={!areas?.length}
        />
      </div>
    </div>
  );
}
